import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { axiosInstance } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import PersonPicker from './PersonPicker';
import { TOP_OF_CHART, UNPLACED, apiError, placementOf, rolesByUser, validManagers } from '../../lib/orgStructure';

const fieldClass = 'h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm';

// The very first role starts at the top of the chart; later ones start unplaced.
const emptyDraft = (defaults = {}, hasRoles = true) => ({
  title: '',
  level: defaults.level ?? 0,
  reportsTo: defaults.parent_id || (hasRoles ? UNPLACED : TOP_OF_CHART),
  team_id: defaults.team_id || '',
  user_id: defaults.user_id || '',
});

const draftFrom = (role) => ({
  title: role.title || '',
  level: Number(role.level || 0),
  reportsTo: role.parent_id || (placementOf(role) === 'root' ? TOP_OF_CHART : UNPLACED),
  team_id: role.team_id || '',
  user_id: role.user_id || '',
});

// Side drawer for creating or editing one role (Org Chart position).
const RoleDrawer = ({ open, onOpenChange, role = null, defaults, positions, people, teams, levels, onSaved }) => {
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reassignTo, setReassignTo] = useState(UNPLACED);
  const editing = Boolean(role?.id);

  useEffect(() => {
    if (!open) return;
    setDraft(editing ? draftFrom(role) : emptyDraft(defaults || {}, positions.length > 0));
    setConfirmDelete(false);
    setReassignTo(role?.parent_id || UNPLACED);
    // Reset only when the drawer opens or switches role, not on every data refresh.
  }, [open, role, defaults, editing]);

  const managers = useMemo(() => validManagers(positions, role?.id)
    .sort((left, right) => (left.level || 0) - (right.level || 0) || (left.title || '').localeCompare(right.title || '')), [positions, role]);
  const directReports = useMemo(() => (role?.id ? positions.filter((position) => position.parent_id === role.id) : []), [positions, role]);
  const roleCounts = useMemo(() => Object.fromEntries(Object.entries(rolesByUser(positions)).map(([id, list]) => [id, list.length])), [positions]);
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) { toast.error('Give the role a title'); return; }
    const placed = draft.reportsTo !== TOP_OF_CHART && draft.reportsTo !== UNPLACED;
    const payload = {
      title: draft.title.trim(),
      level: Number(draft.level),
      parent_id: placed ? draft.reportsTo : null,
      team_id: draft.team_id || null,
      user_id: draft.user_id || null,
      ...(placed ? {} : { placement: draft.reportsTo === TOP_OF_CHART ? 'root' : 'unplaced' }),
    };
    setSaving(true);
    try {
      if (editing) await axiosInstance.put(`/org-positions/${role.id}`, payload);
      else await axiosInstance.post('/org-positions', payload);
      toast.success(editing ? 'Role updated' : 'Role created');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(apiError(error, 'Could not save this role'));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      const params = directReports.length ? { reassign_to: reassignTo === UNPLACED ? 'unplaced' : reassignTo } : {};
      await axiosInstance.delete(`/org-positions/${role.id}`, { params });
      toast.success('Role deleted');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(apiError(error, 'Could not delete this role'));
    } finally { setSaving(false); }
  };

  const reassignOptions = managers.filter((position) => position.id !== role?.id);

  return <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="role-drawer">
      <SheetHeader>
        <SheetTitle>{editing ? 'Edit role' : 'New role'}</SheetTitle>
        <SheetDescription>{editing ? 'Changes show on the Org Chart straight away.' : 'Creates a position on the Org Chart. You can leave it vacant.'}</SheetDescription>
      </SheetHeader>
      <form className="mt-6 space-y-4" onSubmit={save}>
        <label className="block text-sm font-semibold">Role title
          <Input autoFocus value={draft.title} onChange={(event) => set('title', event.target.value)} placeholder="e.g. Head of Customer Support" className="mt-1.5" required />
        </label>
        <label className="block text-sm font-semibold">Level
          <select value={draft.level} onChange={(event) => set('level', Number(event.target.value))} className={`${fieldClass} mt-1.5`}>
            {levels.map((level) => <option key={level.rank} value={level.rank}>{level.label}</option>)}
          </select>
        </label>
        <label className="block text-sm font-semibold">Reports to
          <select value={draft.reportsTo} onChange={(event) => set('reportsTo', event.target.value)} className={`${fieldClass} mt-1.5`} data-testid="role-reports-to">
            <option value={UNPLACED}>Leave unplaced for now</option>
            <option value={TOP_OF_CHART}>Nobody (top of the chart)</option>
            {managers.map((position) => <option key={position.id} value={position.id}>{position.title}{position.user?.name ? ` · ${position.user.name}` : ' · Vacant'}</option>)}
          </select>
        </label>
        <label className="block text-sm font-semibold">Team <span className="font-normal text-slate-500">Optional</span>
          <select value={draft.team_id} onChange={(event) => set('team_id', event.target.value)} className={`${fieldClass} mt-1.5`}>
            <option value="">No team</option>
            {teams.map((team) => <option key={team.id} value={team.id}>{team.name}{team.path ? ` (${team.path})` : ''}</option>)}
          </select>
        </label>
        <div className="text-sm font-semibold">Assign a person <span className="font-normal text-slate-500">Optional</span>
          <div className="mt-1.5"><PersonPicker people={people} value={draft.user_id} onChange={(id) => set('user_id', id)} roleCounts={roleCounts} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" disabled={saving} className="qflow-accent-bg hover:opacity-90">{editing ? 'Save role' : 'Create role'}</Button>
        </div>
      </form>

      {editing && <div className="mt-8 border-t border-slate-200 pt-5">
        {!confirmDelete ? <Button type="button" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setConfirmDelete(true)}><Trash2 size={14} className="mr-2" />Delete role</Button>
          : <div className="space-y-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm" data-testid="role-delete-confirm">
            <p className="font-semibold text-rose-900">Delete “{role.title}”?</p>
            {directReports.length > 0 ? <label className="block text-rose-900">
              {directReports.length} role{directReports.length === 1 ? ' reports' : 's report'} to this one. Move {directReports.length === 1 ? 'it' : 'them'} to:
              <select value={reassignTo} onChange={(event) => setReassignTo(event.target.value)} className={`${fieldClass} mt-1.5`}>
                <option value={UNPLACED}>Unplaced roles</option>
                {reassignOptions.map((position) => <option key={position.id} value={position.id}>{position.title}</option>)}
              </select>
            </label> : <p className="text-rose-900">{role.user?.name ? `${role.user.name} stays in the organisation; only the role is removed.` : 'This vacant role has no direct reports.'}</p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>Keep role</Button>
              <Button type="button" disabled={saving} className="bg-rose-600 hover:bg-rose-700" onClick={remove}>Delete role</Button>
            </div>
          </div>}
      </div>}
    </SheetContent>
  </Sheet>;
};

export default RoleDrawer;
